/**
 * @fileoverview TrackWrapper component for wrapping LiveKit video tracks with position measurement capabilities.
 * 
 * This component provides automatic position tracking for video elements in the recording canvas.
 * It uses ResizeObserver, dimension polling, and window resize events to detect position changes
 * and reports them via callback with debouncing to prevent rapid-fire updates.
 * 
 * Key Features:
 * - Debounced position updates (300ms) to capture final settled positions
 * - Timestamp captured at measurement time for accurate change tracking
 * - Phone rotation detection via dimension polling (250ms interval)
 * - ResizeObserver for automatic size change detection
 * - Window resize event handling
 * - Comprehensive error handling and logging
 * 
 * Position Update Flow:
 * 1. Video element size/position changes
 * 2. ResizeObserver/dimension check/window resize triggers updatePosition()
 * 3. Debounce timer resets (300ms)
 * 4. After 300ms of no new triggers, position is measured via getBoundingClientRect()
 * 5. Timestamp captured immediately at measurement time
 * 6. Position object (with timestamp) passed to onPositionUpdate callback
 * 
 * @module TrackWrapper
 */
import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { View } from '@smarterservices/smarter-ui';
import VideoTrack from './VideoTrack';
import OfflineTrackPlaceholder from './OfflineTrackPlaceholder';
import { useLogger } from '@ui/shared/components/Logger';
import { BACKGROUND_COLOR, LOGGER_LABEL } from '../utils/constants';

/**
 * Timeout in milliseconds before attaching the ResizeObserver
 * This gives the video time to load and stabilize its dimensions
 */
const RESIZE_OBSERVER_TIMEOUT = 500;

/**
 * Debounce delay in milliseconds for position updates
 * This prevents rapid-fire updates during video moves/resizes
 */
const POSITION_UPDATE_DEBOUNCE = 300;

/**
 * Adaptive polling configuration for dimension checks
 * Starts with fast polling and slows down when dimensions are stable
 */
const INITIAL_CHECK_INTERVAL = 250; // Start checking every 250ms
const MAX_CHECK_INTERVAL = 2000; // Slow down to 2s when stable
const INTERVAL_INCREASE_FACTOR = 1.5; // Gradually increase interval
const STABLE_CHECKS_THRESHOLD = 5; // Number of unchanged checks before slowing down

/**
 * TrackWrapper component wraps a video track and measures its position on the canvas.
 * 
 * This component monitors video element position changes through multiple mechanisms:
 * - ResizeObserver for element size changes
 * - Dimension polling (250ms) for video stream dimension changes (e.g., phone rotation)
 * - Window resize events for viewport changes
 * 
 * All position updates are debounced (300ms) to ensure we capture the final settled
 * position rather than intermediate states during animations or transitions.
 *
 * @param {object} props - Component props
 * @param {string} props.trackSid - The LiveKit track SID (unique identifier for this track)
 * @param {object} props.trackRef - The LiveKit track reference object containing participant and publication info
 * @param {function} props.onPositionUpdate - Callback invoked when position changes
 *   @param {string} trackSid - The track SID
 *   @param {object} trackRef - The track reference object
 *   @param {object} position - Position object with structure:
 *     @param {number} position.top - Top position in pixels (rounded)
 *     @param {number} position.left - Left position in pixels (rounded)
 *     @param {number} position.width - Width in pixels (rounded)
 *     @param {number} position.height - Height in pixels (rounded)
 *     @param {string} position.changeDate - ISO timestamp of when position was measured
 * @param {number} props.forceUpdateTrigger - Incrementing value that triggers position remeasurement when changed
 * @returns {React.ReactElement} The rendered component with video track or placeholder
 */
const TrackWrapper = ({ trackSid, trackRef, onPositionUpdate, forceUpdateTrigger = 0 }) => {
  const logger = useLogger();

  /**
   * Ref to the container div element wrapping the video.
   * Currently unused but available for future container-level measurements.
   * @type {React.RefObject<HTMLDivElement>}
   */
  const containerRef = useRef(null);

  /**
   * Ref to the actual video element for position measurement.
   * Used by getBoundingClientRect() to measure position and size.
   * @type {React.RefObject<HTMLVideoElement>}
   */
  const videoRef = useRef(null);

  /**
   * Ref to the ResizeObserver instance that monitors video element size changes.
   * Automatically triggers position updates when video element is resized.
   * @type {React.RefObject<ResizeObserver>}
   */
  const resizeObserverRef = useRef(null);

  /**
   * Ref tracking the last known video stream dimensions (videoWidth x videoHeight).
   * Used to detect dimension changes for phone rotation or stream changes.
   * @type {React.RefObject<{width: number, height: number}>}
   */
  const lastDimensionsRef = useRef({ width: 0, height: 0 });

  /**
   * Ref to the interval ID for dimension checking.
   * Uses adaptive polling that starts fast and slows down when stable.
   * @type {React.RefObject<number>}
   */
  const dimensionCheckIntervalRef = useRef(null);

  /**
   * Ref tracking current polling interval for adaptive dimension checks.
   * Starts at INITIAL_CHECK_INTERVAL and increases to MAX_CHECK_INTERVAL when stable.
   * @type {React.RefObject<number>}
   */
  const currentIntervalRef = useRef(INITIAL_CHECK_INTERVAL);

  /**
   * Ref counting consecutive unchanged dimension checks.
   * Used to determine when to slow down polling frequency.
   * @type {React.RefObject<number>}
   */
  const unchangedCountRef = useRef(0);

  /**
   * Ref to the debounce timer for position updates.
   * Ensures we wait 300ms after last trigger before measuring position.
   * @type {React.RefObject<number>}
   */
  const debounceTimerRef = useRef(null);

  /**
   * Ref to fallback polling interval when ResizeObserver fails.
   * Used as a backup mechanism to track position changes.
   * @type {React.RefObject<number>}
   */
  const fallbackPollingIntervalRef = useRef(null);

  /**
   * Ref tracking last known element size for fallback polling.
   * Used to detect size changes when ResizeObserver is unavailable.
   * @type {React.RefObject<string>}
   */
  const lastSizeRef = useRef('');

  // Log component mount/unmount
  useEffect(() => {
    logger.debug(LOGGER_LABEL, 'TrackWrapper mounted', {
      trackSid,
      hasTrackRef: !!trackRef
    });

    return () => {
      logger.debug(LOGGER_LABEL, 'TrackWrapper unmounted', {
        trackSid
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Force position update when forceUpdateTrigger changes (grid reshuffle)
  useEffect(() => {
    if (forceUpdateTrigger > 0 && videoRef.current) {
      logger.info(LOGGER_LABEL, 'Force update triggered - remeasuring position', {
        trackSid,
        trigger: forceUpdateTrigger
      });

      // Small delay to ensure DOM has settled after track removal
      setTimeout(() => {
        if (videoRef.current) {
          updatePosition();
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdateTrigger]);

  /**
   * Updates the position and size measurement of the video element with debouncing.
   * 
   * This is the core position tracking function that:
   * 1. Implements 300ms debouncing to prevent rapid-fire updates
   * 2. Measures position using getBoundingClientRect()
   * 3. Captures timestamp immediately at measurement time
   * 4. Rounds all position values to nearest pixel
   * 5. Calls onPositionUpdate callback with position data
   * 
   * Debouncing ensures we capture the final settled position after animations,
   * transitions, or rapid resize events complete, rather than intermediate states.
   * 
   * Triggered by:
   * - ResizeObserver on video element size changes
   * - checkVideoDimensions when video stream dimensions change
   * - Window resize events
   * 
   * @function updatePosition
   * @private
   */
  const updatePosition = useCallback(() => {
    try {
      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set a new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        try {
          if (!videoRef.current) {
            logger.warn(LOGGER_LABEL, 'Cannot update position: video ref not available', {
              trackSid
            });
            return;
          }

          const rect = videoRef.current.getBoundingClientRect();
          // Capture timestamp immediately at measurement time
          const measurementTime = new Date().toISOString();

          // round the position values, to nearest pixel...
          const positionObject = {
            top: Math.round(rect.top),
            left: Math.round(rect.left),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
            changeDate: measurementTime, // Include timestamp from measurement time
          };

          logger.debug(LOGGER_LABEL, 'Position updated (after debounce)', {
            trackSid,
            position: positionObject
          });

          // Send update FIRST, then reconnect observer
          onPositionUpdate(trackSid, trackRef, positionObject);

          // Reconnect ResizeObserver instead of disconnecting
          // This ensures we don't miss subsequent changes during debounce period
          if (resizeObserverRef.current && videoRef.current) {
            try {
              resizeObserverRef.current.disconnect();
              resizeObserverRef.current.observe(videoRef.current);
              logger.debug(LOGGER_LABEL, 'ResizeObserver reconnected after update', {
                trackSid
              });
            } catch (reconnectError) {
              logger.error(LOGGER_LABEL, 'Failed to reconnect ResizeObserver', {
                error: reconnectError,
                errorMessage: reconnectError.message,
                trackSid
              });
            }
          }
        } catch (innerError) {
          logger.error(LOGGER_LABEL, 'Error in debounced position update', {
            error: innerError,
            errorMessage: innerError.message,
            trackSid
          });
        }
      }, POSITION_UPDATE_DEBOUNCE);
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error setting up debounced position update', {
        error,
        errorMessage: error.message,
        trackSid
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPositionUpdate, trackSid, trackRef]);

  /**
   * Checks for video dimension changes to detect phone rotation or stream changes.
   * 
   * This function polls the video element's videoWidth and videoHeight properties
   * to detect when the video stream dimensions change (e.g., when a phone rotates
   * from portrait to landscape). When a change is detected, it triggers a debounced
   * position update.
   * 
   * Uses adaptive polling that starts fast (250ms) and slows down to (2s) when stable
   * to reduce CPU usage while maintaining responsiveness to actual changes.
   * 
   * @function checkVideoDimensions
   * @private
   */
  const checkVideoDimensions = useCallback(() => {
    try {
      if (videoRef.current && videoRef.current.videoWidth && videoRef.current.videoHeight) {
        const currentWidth = videoRef.current.videoWidth;
        const currentHeight = videoRef.current.videoHeight;
        const lastDimensions = lastDimensionsRef.current;

        if (currentWidth !== lastDimensions.width || currentHeight !== lastDimensions.height) {
          logger.info(LOGGER_LABEL, 'Video dimensions changed (rotation detected)', {
            trackSid,
            oldWidth: lastDimensions.width,
            oldHeight: lastDimensions.height,
            newWidth: currentWidth,
            newHeight: currentHeight,
            previousInterval: currentIntervalRef.current
          });

          lastDimensionsRef.current = { width: currentWidth, height: currentHeight };

          // Reset to fast polling when change detected
          currentIntervalRef.current = INITIAL_CHECK_INTERVAL;
          unchangedCountRef.current = 0;

          // Restart interval with fast polling
          if (dimensionCheckIntervalRef.current) {
            clearInterval(dimensionCheckIntervalRef.current);
            dimensionCheckIntervalRef.current = setInterval(
              checkVideoDimensions,
              currentIntervalRef.current
            );
          }

          logger.debug(LOGGER_LABEL, 'Reset to fast polling', {
            trackSid,
            interval: currentIntervalRef.current
          });

          // Video dimensions changed, trigger debounced position update
          updatePosition();
        } else {
          // No change detected - increment unchanged counter
          unchangedCountRef.current += 1;

          // After STABLE_CHECKS_THRESHOLD consecutive unchanged checks, slow down polling
          if (unchangedCountRef.current >= STABLE_CHECKS_THRESHOLD) {
            const newInterval = Math.min(
              currentIntervalRef.current * INTERVAL_INCREASE_FACTOR,
              MAX_CHECK_INTERVAL
            );

            // Only restart interval if we're actually changing the frequency
            if (newInterval !== currentIntervalRef.current) {
              currentIntervalRef.current = newInterval;
              unchangedCountRef.current = 0; // Reset counter

              // Restart interval with slower polling
              if (dimensionCheckIntervalRef.current) {
                clearInterval(dimensionCheckIntervalRef.current);
                dimensionCheckIntervalRef.current = setInterval(
                  checkVideoDimensions,
                  currentIntervalRef.current
                );
              }

              logger.debug(LOGGER_LABEL, 'Slowed down polling (dimensions stable)', {
                trackSid,
                newInterval: currentIntervalRef.current,
                maxInterval: MAX_CHECK_INTERVAL
              });
            }
          }
        }
      }
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error checking video dimensions', {
        error,
        errorMessage: error.message,
        trackSid
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePosition, trackSid]);

  /**
   * Handles video loaded event and sets up position tracking mechanisms.
   * 
   * This function is called by the VideoTrack component when the video element
   * has loaded and is ready for measurement. It:
   * 1. Waits 500ms for video to stabilize
   * 2. Creates and attaches ResizeObserver to video element
   * 3. Initializes dimension tracking for rotation detection
   * 4. Starts 250ms polling interval for dimension changes
   * 
   * The 500ms delay ensures the video has fully loaded and its dimensions
   * are stable before we start tracking position changes.
   * 
   * @function handleVideoLoaded
   * @callback
   * @private
   */
  const handleVideoLoaded = useCallback(() => {
    try {
      logger.debug(LOGGER_LABEL, 'Video loaded, setting up ResizeObserver', {
        trackSid,
        timeout: RESIZE_OBSERVER_TIMEOUT
      });

      // Wait before attaching ResizeObserver to allow video to stabilize
      setTimeout(() => {
        try {
          if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
          }

          if (videoRef.current) {
            // Try to use ResizeObserver for optimal performance
            try {
              resizeObserverRef.current = new ResizeObserver(updatePosition);
              resizeObserverRef.current.observe(videoRef.current);

              logger.debug(LOGGER_LABEL, 'ResizeObserver attached successfully', {
                trackSid
              });
            } catch (observerError) {
              logger.error(LOGGER_LABEL, 'ResizeObserver failed, falling back to polling', {
                error: observerError,
                errorMessage: observerError.message,
                trackSid
              });

              // Fallback: Poll for size changes every 500ms
              // This ensures position tracking continues even if ResizeObserver fails
              const pollForSizeChanges = () => {
                if (!videoRef.current) {
                  if (fallbackPollingIntervalRef.current) {
                    clearInterval(fallbackPollingIntervalRef.current);
                    fallbackPollingIntervalRef.current = null;
                  }
                  return;
                }

                const rect = videoRef.current.getBoundingClientRect();
                const currentSize = `${Math.round(rect.width)}x${Math.round(rect.height)}`;

                if (lastSizeRef.current && lastSizeRef.current !== currentSize) {
                  logger.debug(LOGGER_LABEL, 'Fallback polling detected size change', {
                    trackSid,
                    oldSize: lastSizeRef.current,
                    newSize: currentSize
                  });
                  updatePosition();
                }

                lastSizeRef.current = currentSize;
              };

              // Initialize with current size
              if (videoRef.current) {
                const rect = videoRef.current.getBoundingClientRect();
                lastSizeRef.current = `${Math.round(rect.width)}x${Math.round(rect.height)}`;
              }

              // Start fallback polling
              fallbackPollingIntervalRef.current = setInterval(pollForSizeChanges, 500);

              logger.info(LOGGER_LABEL, 'Fallback polling started', {
                trackSid,
                intervalMs: 500
              });
            }

            // Initialize dimension tracking
            if (videoRef.current.videoWidth && videoRef.current.videoHeight) {
              lastDimensionsRef.current = {
                width: videoRef.current.videoWidth,
                height: videoRef.current.videoHeight
              };

              logger.debug(LOGGER_LABEL, 'Initial dimensions tracked', {
                trackSid,
                width: videoRef.current.videoWidth,
                height: videoRef.current.videoHeight
              });
            }

            // Start adaptive polling for dimension changes (for phone rotation detection)
            // Begins with fast polling (250ms) and slows down when dimensions are stable
            if (dimensionCheckIntervalRef.current) {
              clearInterval(dimensionCheckIntervalRef.current);
            }

            // Reset adaptive polling state
            currentIntervalRef.current = INITIAL_CHECK_INTERVAL;
            unchangedCountRef.current = 0;

            dimensionCheckIntervalRef.current = setInterval(
              checkVideoDimensions,
              INITIAL_CHECK_INTERVAL
            );

            logger.debug(LOGGER_LABEL, 'Adaptive dimension check interval started', {
              trackSid,
              initialIntervalMs: INITIAL_CHECK_INTERVAL,
              maxIntervalMs: MAX_CHECK_INTERVAL
            });
          } else {
            logger.warn(LOGGER_LABEL, 'Video ref not available after timeout', {
              trackSid
            });
          }
        } catch (timeoutError) {
          logger.error(LOGGER_LABEL, 'Error in ResizeObserver setup timeout', {
            error: timeoutError,
            errorMessage: timeoutError.message,
            trackSid
          });
        }
      }, RESIZE_OBSERVER_TIMEOUT);
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error in handleVideoLoaded', {
        error,
        errorMessage: error.message,
        trackSid
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePosition, checkVideoDimensions, trackSid]);

  /**
   * Cleanup effect that runs on component unmount.
   * 
   * Properly cleans up all tracking mechanisms to prevent memory leaks:
   * - Disconnects ResizeObserver
   * - Clears dimension check interval
   * - Clears any pending debounce timer
   * 
   * @effect
   */
  useEffect(() => {
    return () => {
      try {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          logger.debug(LOGGER_LABEL, 'ResizeObserver disconnected on cleanup', {
            trackSid
          });
        }
        if (dimensionCheckIntervalRef.current) {
          clearInterval(dimensionCheckIntervalRef.current);
          logger.debug(LOGGER_LABEL, 'Dimension check interval cleared on cleanup', {
            trackSid
          });
        }
        if (fallbackPollingIntervalRef.current) {
          clearInterval(fallbackPollingIntervalRef.current);
          logger.debug(LOGGER_LABEL, 'Fallback polling interval cleared on cleanup', {
            trackSid
          });
        }
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          logger.debug(LOGGER_LABEL, 'Debounce timer cleared on cleanup', {
            trackSid
          });
        }
      } catch (error) {
        logger.error(LOGGER_LABEL, 'Error during cleanup', {
          error,
          errorMessage: error.message,
          trackSid
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Window resize event listener effect.
   * 
   * Attaches a listener to window resize events to trigger position updates
   * when the browser window is resized. This ensures position tracking works
   * correctly when the viewport dimensions change.
   * 
   * The updatePosition function is debounced, so rapid window resize events
   * won't cause excessive position updates.
   * 
   * @effect
   */
  useEffect(() => {
    try {
      window.addEventListener('resize', updatePosition);
      logger.debug(LOGGER_LABEL, 'Window resize listener added', {
        trackSid
      });
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error adding window resize listener', {
        error,
        errorMessage: error.message,
        trackSid
      });
    }

    return () => {
      try {
        window.removeEventListener('resize', updatePosition);
        logger.debug(LOGGER_LABEL, 'Window resize listener removed', {
          trackSid
        });
      } catch (error) {
        logger.error(LOGGER_LABEL, 'Error removing window resize listener', {
          error,
          errorMessage: error.message,
          trackSid
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePosition, trackSid]);

  return (
    <View
      as="div"
      width="100%"
      height="100%"
      overflow="hidden"
      position="relative"
      border="none"
      borderRadius="medium"
      background="secondary"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          backgroundColor: BACKGROUND_COLOR,
        }}
      >
        {trackRef ? (
          <VideoTrack
            trackRef={trackRef}
            videoRef={videoRef}
            onVideoLoaded={handleVideoLoaded}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            dataTrackSid={trackSid}
          />
        ) : (
          <OfflineTrackPlaceholder trackSid={trackSid} />
        )}
      </div>
    </View>
  );
};

/**
 * PropTypes validation for TrackWrapper component.
 * 
 * @property {string} trackSid - Required. The LiveKit track SID identifier
 * @property {object} trackRef - Optional. The LiveKit track reference object
 * @property {function} onPositionUpdate - Optional. Callback for position updates
 * @property {number} forceUpdateTrigger - Optional. Incrementing value that triggers position remeasurement
 */
TrackWrapper.propTypes = {
  trackSid: PropTypes.string.isRequired,
  trackRef: PropTypes.object,
  onPositionUpdate: PropTypes.func,
  forceUpdateTrigger: PropTypes.number,
};

export default TrackWrapper;
